/**
 * Local Agent tab — upload a new version of the Companion / Local Agent
 * ZIP. Chunked upload (4MB) to bypass ingress body limits. Only the
 * latest version is kept; stores download it in-app from
 * /api/companion/agent-latest.zip.
 */
import { useEffect, useRef, useState } from "react";
import { supHttp } from "@/lib/SupervisorApi";
import { toast } from "sonner";
import { MonitorDown, UploadCloud, Download, RefreshCw, FileArchive, CheckCircle2 } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 4 * 1024 * 1024;

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

export default function AgentTab() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await supHttp.get("/agent-file/info");
      setInfo(r.data);
    } catch {
      toast.error("Σφάλμα φόρτωσης πληροφοριών agent");
    }
  };
  useEffect(() => { refresh(); }, []);

  const onUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Επίλεξε αρχείο .zip");
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0")).join("");
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await supHttp.post("/agent-file/chunk", fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const r = await supHttp.post("/agent-file/finalize", {
        upload_id: uploadId,
        filename: file.name,
      });
      toast.success(`Ανέβηκε νέα έκδοση: ${r.data.orig_filename} (${fmtBytes(r.data.size)})`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία ανεβάσματος");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="sup-agent-tab">
      <div className="flex items-center gap-2 text-zinc-200">
        <MonitorDown size={18} className="text-amber-400" />
        <h2 className="font-heading font-bold text-lg">Local Agent — Διαχείριση Έκδοσης</h2>
      </div>

      {/* Current version */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
        <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Τρέχουσα έκδοση</div>
        {info ? (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <FileArchive size={28} className={info.uploaded ? "text-emerald-400" : "text-zinc-500"} />
              <div>
                <div className="font-bold text-zinc-100" data-testid="sup-agent-filename">
                  {info.filename || "— κανένα αρχείο —"}
                </div>
                <div className="text-xs text-zinc-500">
                  {fmtBytes(info.size)}
                  {info.uploaded_at && ` · ${new Date(info.uploaded_at).toLocaleString("el-GR")}`}
                  {info.uploaded_by && ` · από ${info.uploaded_by}`}
                  {!info.uploaded && " · ενσωματωμένο στην εφαρμογή (δεν έχει ανέβει νέα έκδοση)"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`${BASE}/api/companion/agent-latest.zip`}
                data-testid="sup-agent-download"
                className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold flex items-center gap-1.5 no-underline"
              >
                <Download size={14} /> Λήψη
              </a>
              <button
                onClick={refresh}
                data-testid="sup-agent-refresh"
                className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold flex items-center gap-1.5"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-zinc-500 text-sm">Φόρτωση…</div>
        )}
      </div>

      {/* Upload */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider font-bold text-zinc-500">Ανέβασμα νέας έκδοσης (.zip)</div>
        <input
          ref={fileRef}
          type="file"
          accept=".zip,application/zip"
          disabled={busy}
          onChange={(e) => onUpload(e.target.files?.[0])}
          data-testid="sup-agent-upload-input"
          className="block w-full text-sm text-zinc-400 file:mr-4 file:h-10 file:px-4 file:rounded-lg file:border-0 file:bg-amber-500 file:text-zinc-950 file:font-bold file:cursor-pointer hover:file:bg-amber-400"
        />
        {busy && (
          <div className="space-y-1.5">
            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${progress}%` }}
                data-testid="sup-agent-progress"
              />
            </div>
            <div className="text-xs text-zinc-400 flex items-center gap-1.5">
              <UploadCloud size={13} className="animate-pulse" /> Ανέβασμα… {progress}%
            </div>
          </div>
        )}
        <div className="text-xs text-zinc-500 flex items-start gap-1.5">
          <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-500" />
          <span>
            Μετά το ανέβασμα, το κουμπί «Λήψη Local Agent» μέσα στην εφαρμογή (Ρυθμίσεις → Εκτυπωτές)
            κατεβάζει αυτόματα τη νέα έκδοση σε όλα τα μαγαζιά. Κρατείται μόνο η τελευταία έκδοση.
          </span>
        </div>
      </div>
    </div>
  );
}
